import { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space } from 'antd';
import { AdminService } from '../Services/AdminService';
import { message } from 'antd';
import { TextService } from '../Services/TextService';
import { IntegrationType } from '../Types/IntegrationType';
import { WebApiAdminDtoInvoiceGenerationRequest } from '../Repository/eventbookyapi';

interface Props {
    visible: boolean;
    companyId: number | null;
    companyName: string;
    subscriptionLevel: number;
    onCancel: () => void;
}

const AdminInvoiceForm = ({ visible, companyId, companyName, onCancel }: Props) => {
    const [form] = Form.useForm<WebApiAdminDtoInvoiceGenerationRequest>();
    const [loading, setLoading] = useState(false);
    const GetText = TextService.GetText;

    const handleSubmit = async (values: WebApiAdminDtoInvoiceGenerationRequest) => {
        if (!companyId) return;
        setLoading(true);
        try {
            await AdminService.GenerateInvoicePdf({
                ...values,
                companyId,
                forceIntegrationType: values.forceIntegrationType ?? undefined,
            });
            message.success(GetText("AdminInvoice_Success"));
            form.resetFields();
            onCancel();
        } catch {
            message.error(GetText("AdminInvoice_Error"));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title={`${GetText("AdminInvoice_Title")} – ${companyName}`}
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={560}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ marginTop: 12 }}
            >
                <Form.Item
                    name="invoiceNumber"
                    label={GetText("AdminInvoice_InvoiceNumber")}
                    rules={[{ required: true, message: GetText("AdminInvoice_InvoiceNumberRequired") }]}
                >
                    <Input placeholder={GetText("AdminInvoice_InvoiceNumberPlaceholder")} />
                </Form.Item>

                <Form.Item label={GetText("AdminInvoice_ReferenceName")} name="customerReferenceName" rules={[{ required: true, message: GetText("AdminInvoice_ReferenceNameRequired") }]}>
                    <Input placeholder={GetText("AdminInvoice_ReferenceNamePlaceholder")} />
                </Form.Item>

                <Form.Item label={GetText("AdminInvoice_ReferenceAddress")} name="customerReferenceAddress" rules={[{ required: true, message: GetText("AdminInvoice_ReferenceAddressRequired") }]}>
                    <Input placeholder={GetText("AdminInvoice_ReferenceAddressPlaceholder")} />
                </Form.Item>

                <Space.Compact style={{ width: '100%', display: 'flex', gap: 8 }}>
                    <Form.Item label={GetText("AdminInvoice_ZipCode")} name="customerReferenceZipCode" style={{ flex: '0 0 140px' }} rules={[{ required: true, message: GetText("AdminInvoice_ZipCodeRequired") }]}>
                        <Input placeholder={GetText("AdminInvoice_ZipCodePlaceholder")} />
                    </Form.Item>
                    <Form.Item label={GetText("AdminInvoice_City")} name="customerReferenceCity" style={{ flex: 1 }} rules={[{ required: true, message: GetText("AdminInvoice_CityRequired") }]}>
                        <Input placeholder={GetText("AdminInvoice_CityPlaceholder")} />
                    </Form.Item>
                </Space.Compact>

                <Form.Item
                    label={GetText("AdminInvoice_SubscriptionPrice")}
                    name="subscriptionPriceOverride"
                >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder={GetText("AdminInvoice_SubscriptionPricePlaceholder")} />
                </Form.Item>

                <Form.Item
                    label={GetText("AdminInvoice_IntegrationPrice")}
                    name="integrationPriceOverride"
                >
                    <InputNumber min={0} style={{ width: '100%' }} placeholder={GetText("AdminInvoice_IntegrationPricePlaceholder")} />
                </Form.Item>

                <Form.Item
                    label={GetText("AdminInvoice_ForceIntegration")}
                    name="forceIntegrationType"
                    extra={GetText("AdminInvoice_ForceIntegrationHint")}
                >
                    <Select allowClear placeholder={GetText("AdminInvoice_ForceIntegrationPlaceholder")} style={{ width: '100%' }}>
                        <Select.Option value={IntegrationType.Fortnox}>Fortnox</Select.Option>
                        <Select.Option value={IntegrationType.Spiris}>Spiris</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                        <Button onClick={handleCancel}>{GetText("AdminInvoice_Cancel")}</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {GetText("AdminInvoice_GeneratePdf")}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AdminInvoiceForm;
